import { drawExtensionIcon } from '@/extensions/pixelIcons';
import {
  clockedConfig,
  clockedRuntime,
  clockIo,
  clockModeItem,
  clockNode,
  clockedBuild,
  watchSampledInput,
} from '../clocked';
import { z } from 'zod';
import { reaction } from 'mobx';
import { configureTile } from '../configuration';
import type { DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import type { TileDefinition } from '@/models/tiles/TileDefinition';
import { InputType } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const KEYBOARD_TYPE = 'extension:digitiles.keyboard/key' as const;
const codeSchema = z
  .string()
  .regex(
    /^[A-Za-z][A-Za-z0-9]{0,31}$/,
    'Use a keyboard code such as KeyK, Digit1 or ArrowUp.',
  );
const stateSchema = z.object({ code: codeSchema, ...clockedConfig });
type KeyboardConfig = z.infer<typeof stateSchema>;
const nodeSchema = stateSchema.extend({
  ...clockedRuntime,
  code: codeSchema,
  pressed: z.boolean(),
  enabled: z.boolean(),
});
type KeyboardRuntime = z.infer<typeof nodeSchema>;
const ioSides = {
  [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: clockIo,
  [Side.LEFT]: { type: TileSideIOType.NONE, forceConnection: false },
};
const tile: TileDefinition<KeyboardConfig> = {
  type: KEYBOARD_TYPE,
  name: 'Keyboard key',
  canRotate: true,
  stateSchema,
  createDefaultState: () => ({ code: 'KeyK', clocked: false }),
  graphConfiguration: (state) => ({ code: state.code, clocked: state.clocked }),
  ioSides,
  buildAndConnectNodes: clockedBuild(KEYBOARD_TYPE, false, true),
  createContextMenuItems: ({ instance, chipEditorModel }) => [
    clockModeItem({ instance, chipEditorModel }),
    {
      label: `Choose key (${instance.state.code})`,
      action: async (takeInput) => {
        try {
          const code = await takeInput(
            InputType.STRING,
            'Key code (for example KeyK)',
            instance.state.code,
            true,
            (value) => ({
              isValid: codeSchema.safeParse(value).success,
              errorMessage: 'Use a keyboard code such as KeyK, Digit1 or ArrowUp.',
            }),
          );
          configureTile(
            instance,
            chipEditorModel,
            { code: codeSchema.parse(code) },
            'Change keyboard key',
          );
        } catch {
          /* Cancelling keeps the selected key. */
        }
      },
    },
  ],
  graphics: {
    type: KEYBOARD_TYPE,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );
      drawExtensionIcon(g, 'keyboard', meta.outputs.right ? 0x8bd6ff : 0x8192a4);
    },
  },
};

function isTextTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    !!target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]',
    )
  );
}

export const keyboardExtension: DigitilesExtension = {
  id: 'digitiles.keyboard',
  name: 'Keyboard input',
  version: '1.1.0',
  description:
    'Read a physical key live, or sample it on a rising bottom clock using the tile menu. Text fields, dialogs and editor shortcuts keep their normal behavior.',
  capabilities: ['keyboard'],
  tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
  nodes: [
    {
      type: KEYBOARD_TYPE,
      stateSchema: nodeSchema.transform((state) => ({
        ...state,
        sampled: false,
        pressed: false,
        enabled: false,
      })),
      configurationKeys: ['code', 'enabled', 'clocked'],
      createDefaultState: (): KeyboardRuntime => ({
        clocked: false,
        sampled: false,
        code: 'KeyK',
        pressed: false,
        enabled: false,
      }),
      process: (_input, state: KeyboardRuntime) =>
        state.enabled && (state.clocked ? state.sampled : state.pressed),
    },
    clockNode(KEYBOARD_TYPE),
  ],
  activate(app, signal) {
    const held = new Set<string>();
    let active: ActiveExtensionNode[] = [];
    let byCode = new Map<string, ActiveExtensionNode[]>();
    const stopClock = watchSampledInput(
      app,
      KEYBOARD_TYPE,
      (target) => target.node.state.pressed,
    );
    const assign = (target: ActiveExtensionNode, enabled: boolean, pressed: boolean) => {
      const state = target.node.state as KeyboardRuntime;
      if (state.enabled === enabled && state.pressed === pressed) return;
      const changedEnable = state.enabled !== enabled;
      state.enabled = enabled;
      if (!enabled) state.sampled = false;
      state.pressed = pressed;
      if (
        (!state.clocked || changedEnable) &&
        target.simulation.graph.nodes.get(target.node.id) === target.node
      )
        target.simulation.triggerUpdate(target.node.id);
    };
    const reset = () => {
      held.clear();
      for (const target of active) assign(target, true, false);
    };
    const unwatch = watchActiveExtensionNodes(app, KEYBOARD_TYPE, (nodes) => {
      const retained = new Set(nodes.map((target) => target.node));
      for (const target of active)
        if (!retained.has(target.node)) assign(target, false, false);
      active = nodes;
      byCode = new Map();
      for (const target of active) {
        const code = (target.node.state as KeyboardRuntime).code;
        let group = byCode.get(code);
        if (!group) byCode.set(code, (group = []));
        group.push(target);
        assign(target, true, held.has(code));
      }
    });
    const keydown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        app.uiModel.inDialog ||
        isTextTarget(event.target) ||
        document.hidden
      )
        return;
      held.add(event.code);
      for (const target of byCode.get(event.code) ?? []) assign(target, true, true);
    };
    const keyup = (event: KeyboardEvent) => {
      held.delete(event.code);
      for (const target of byCode.get(event.code) ?? []) assign(target, true, false);
    };
    window.addEventListener('keydown', keydown, { signal });
    window.addEventListener('keyup', keyup, { signal });
    window.addEventListener('blur', reset, { signal });
    document.addEventListener('visibilitychange', reset, { signal });
    document.addEventListener(
      'focusin',
      (event) => {
        if (isTextTarget(event.target)) reset();
      },
      { signal },
    );
    const stopDialogWatch = reaction(
      () => app.uiModel.inDialog,
      (inDialog) => {
        if (inDialog) reset();
      },
    );
    return () => {
      stopDialogWatch();
      held.clear();
      stopClock();
      unwatch();
    };
  },
};
